'use client'

import React, { Suspense } from 'react'
import { Routes, Route } from 'react-router-dom'

import { Layout } from './components/common/layout'
import { ScrollToTop } from './components/common/ScrollToTop'
import { Callback } from './components/pages/Callback'
import { Home } from './components/pages/Home'

// Lazy load route components
const Pattern = React.lazy(() => import('./components/pages/Pattern').then((m) => ({ default: m.Pattern })))
const CreatePattern = React.lazy(() =>
  import('./components/pages/CreatePattern').then((m) => ({ default: m.CreatePattern })),
)
const PatternPreview = React.lazy(() =>
  import('./components/pages/CreatePattern').then((m) => ({ default: m.PatternPreview })),
)
const EditPattern = React.lazy(() => import('./components/pages/EditPattern').then((m) => ({ default: m.EditPattern })))
const Profile = React.lazy(() => import('./components/pages/Profile').then((m) => ({ default: m.Profile })))
const CreateProfile = React.lazy(() =>
  import('./components/pages/CreateProfile').then((m) => ({ default: m.CreateProfile })),
)
const EditProfile = React.lazy(() => import('./components/pages/EditProfile').then((m) => ({ default: m.EditProfile })))
const UserSettings = React.lazy(() => import('./components/pages/UserSettings/UserSettings'))
const NotificationCenter = React.lazy(() =>
  import('./components/pages/NotificationCenter').then((m) => ({ default: m.NotificationCenter })),
)
const SearchResults = React.lazy(() =>
  import('./components/pages/SearchResults').then((m) => ({ default: m.SearchResults })),
)
const ModerationDashboard = React.lazy(() =>
  import('./components/pages/Moderation/UnifiedModerationDashboard').then((m) => ({
    default: m.UnifiedModerationDashboard,
  })),
)
const ModerationPreview = React.lazy(() =>
  import('./components/pages/Moderation/ModerationPreview').then((m) => ({
    default: m.ModerationPreview,
  })),
)
const MaterialContributionReviewPage = React.lazy(
  () => import('./components/pages/Moderation/MaterialContributionReviewPage'),
)
const UserAnalysisPage = React.lazy(() =>
  import('./components/pages/UserAnalysis').then((m) => ({ default: m.UserAnalysisPage })),
)
const Roadmap = React.lazy(() => import('./components/pages/Roadmap').then((m) => ({ default: m.Roadmap })))
const SupportingArtistsPage = React.lazy(() =>
  import('./components/pages/SupportingArtistsPage').then((m) => ({ default: m.SupportingArtistsPage }))
)
const CraftRoom = React.lazy(() => import('./components/pages/CraftRoom').then((m) => ({ default: m.CraftRoom })))
const CreateCraftProjectPage = React.lazy(() =>
  import('./components/pages/CraftRoom/CreateCraftProjectPage').then((m) => ({
    default: m.CreateCraftProjectPage,
  })),
)
const CraftProjectDetailPage = React.lazy(() => import('./components/pages/CraftRoom/CraftProjectDetailPage'))
const EditCraftProjectPage = React.lazy(() => import('./components/pages/CraftRoom/EditCraftProjectPage'))

const MaterialCreationPage = React.lazy(() => import('./components/pages/CraftRoom/MaterialCreationPage'))
const PublicMaterialDetailPage = React.lazy(() => import('./components/pages/Material/PublicMaterialPage'))
const PersonalMaterialDetailPage = React.lazy(() => import('./components/pages/CraftRoom/PersonalMaterialPage'))
const EditPersonalMaterialPage = React.lazy(() => import('./components/pages/CraftRoom/EditPersonalMaterialPage'))
const InventoryPage = React.lazy(() =>
  import('./components/pages/Inventory/InventoryPage').then((m) => ({ default: m.InventoryPage })),
)
const PublicMaterialsTestPage = React.lazy(() => import('./pages/PublicMaterialsTestPage'))
const CreateParentMaterialPage = React.lazy(() => import('./pages/CreateParentMaterialPage'))
const LinkMaterialPage = React.lazy(() => import('./pages/LinkMaterialPage'))
const PendingLinkConfirmationsPage = React.lazy(() => import('./pages/PendingLinkConfirmationsPage'))
const MaterialDiscoveryPage = React.lazy(() => import('./components/pages/MaterialDiscoveryPage'))

const AppealQueue = React.lazy(() =>
  import('./components/pages/Moderation/AppealQueue').then((m) => ({ default: m.AppealQueue })),
)
const PurchaseLinksPage = React.lazy(() =>
  import('./components/pages/Moderation/PurchaseLinksPage').then((m) => ({
    default: m.PurchaseLinksPage,
  })),
)
const AnalyticsDashboard = React.lazy(() =>
  import('./components/pages/Moderation/AnalyticsDashboard').then((m) => ({
    default: m.AnalyticsDashboard,
  })),
)
const ModerationHistory = React.lazy(() =>
  import('./components/pages/Moderation/History/ModerationHistory').then((m) => ({
    default: m.ModerationHistory,
  })),
)
const MyCollectionsPage = React.lazy(() =>
  import('./components/pages/Collections').then((m) => ({ default: m.MyCollectionsPage })),
)
const CollectionViewPage = React.lazy(() =>
  import('./components/pages/Collections').then((m) => ({ default: m.CollectionViewPage })),
)
const CollectionSearchPage = React.lazy(() =>
  import('./components/pages/Collections').then((m) => ({ default: m.CollectionSearchPage })),
)
const BrandPage = React.lazy(() => import('./pages/BrandPage').then((m) => ({ default: m.BrandPage })))
const MyFieldSuggestionsPage = React.lazy(() => import('./components/pages/CraftRoom/MyFieldSuggestionsPage'))

// Loading component
const PageLoader = () => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '200px',
      color: '#666',
    }}
  >
    Loading...
  </div>
)

import './App.css'
import './styles/dnd-fixes.css'
import './styles/loading.css'

import { AuthRoute } from './components/common/AuthRoute'
import { ModeratorRoute } from './components/common/ModeratorRoute'
import { usePageViewBeacon } from './hooks/usePageViewBeacon'
// import "./styles/profile-theme.css";

function PageViewBeaconEmitter() {
  usePageViewBeacon()
  return null
}

const App = () => {
  // DEBUG: App component debugging
  console.log('[APP] hostname:', window.location.hostname)
  console.log('[APP] always showing gate now')

  const appContent = (
    <>
      <PageViewBeaconEmitter />
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<Home />} />
          <Route path="callback" element={<Callback />} />

          <Route
            path="pattern/:patternId/:slug?"
            element={
              <Suspense fallback={<PageLoader />}>
                <Pattern />
              </Suspense>
            }
          />
          <Route
            path="pattern/draft/preview/:patternId"
            element={
              <Suspense fallback={<PageLoader />}>
                <PatternPreview />
              </Suspense>
            }
          />
          <Route
            path="pattern/temp/preview/:patternId"
            element={
              <Suspense fallback={<PageLoader />}>
                <PatternPreview />
              </Suspense>
            }
          />
          <Route
            path="pattern/draft-review/:patternId"
            element={
              <Suspense fallback={<PageLoader />}>
                <AuthRoute><PatternPreview /></AuthRoute>
              </Suspense>
            }
          />
          <Route
            path="author/:screenName"
            element={
              <Suspense fallback={<PageLoader />}>
                <Profile />
              </Suspense>
            }
          />
          <Route
            path="create"
            element={
              <Suspense fallback={<PageLoader />}>
                <AuthRoute><CreatePattern /></AuthRoute>
              </Suspense>
            }
          />
          <Route
            path="edit/:patternId/:slug?"
            element={
              <Suspense fallback={<PageLoader />}>
                <AuthRoute><EditPattern /></AuthRoute>
              </Suspense>
            }
          />
          <Route
            path="create/profile"
            element={
              <Suspense fallback={<PageLoader />}>
                <AuthRoute><CreateProfile /></AuthRoute>
              </Suspense>
            }
          />
          <Route
            path="edit/profile/:screenName"
            element={
              <Suspense fallback={<PageLoader />}>
                <AuthRoute><EditProfile /></AuthRoute>
              </Suspense>
            }
          />
          <Route
            path="settings"
            element={
              <Suspense fallback={<PageLoader />}>
                <AuthRoute><UserSettings /></AuthRoute>
              </Suspense>
            }
          />
          <Route
            path="notifications"
            element={
              <Suspense fallback={<PageLoader />}>
                <AuthRoute><NotificationCenter /></AuthRoute>
              </Suspense>
            }
          />
          <Route
            path="search"
            element={
              <Suspense fallback={<PageLoader />}>
                <SearchResults />
              </Suspense>
            }
          />
          <Route
            path="moderation"
            element={
              <Suspense fallback={<PageLoader />}>
                <ModeratorRoute><ModerationDashboard /></ModeratorRoute>
              </Suspense>
            }
          />
          <Route
            path="moderation/appeals"
            element={
              <Suspense fallback={<PageLoader />}>
                <ModeratorRoute><AppealQueue /></ModeratorRoute>
              </Suspense>
            }
          />
          <Route
            path="moderation/purchase-links"
            element={
              <Suspense fallback={<PageLoader />}>
                <ModeratorRoute><PurchaseLinksPage /></ModeratorRoute>
              </Suspense>
            }
          />
          <Route
            path="moderation/analytics"
            element={
              <Suspense fallback={<PageLoader />}>
                <ModeratorRoute><AnalyticsDashboard /></ModeratorRoute>
              </Suspense>
            }
          />
          <Route
            path="moderation/history"
            element={
              <Suspense fallback={<PageLoader />}>
                <ModeratorRoute><ModerationHistory /></ModeratorRoute>
              </Suspense>
            }
          />
          <Route
            path="moderation/preview/:snapshotId"
            element={
              <Suspense fallback={<PageLoader />}>
                <ModeratorRoute><ModerationPreview /></ModeratorRoute>
              </Suspense>
            }
          />
          <Route
            path="moderation/material-contribution/:contributionId"
            element={
              <Suspense fallback={<PageLoader />}>
                <ModeratorRoute><MaterialContributionReviewPage /></ModeratorRoute>
              </Suspense>
            }
          />
          <Route
            path="moderation/user/:userId/analysis"
            element={
              <Suspense fallback={<PageLoader />}>
                <ModeratorRoute><UserAnalysisPage /></ModeratorRoute>
              </Suspense>
            }
          />
          <Route
            path="roadmap"
            element={
              <Suspense fallback={<PageLoader />}>
                <Roadmap />
              </Suspense>
            }
          />
          <Route
            path="craft-rooms/:screenName"
            element={
              <Suspense fallback={<PageLoader />}>
                {/*
                  Public view: anyone may view a craft room; privacy settings control WHAT is shown,
                  not WHETHER it can be accessed. The CraftRoom component branches owner-vs-visitor and
                  loads visitor data via the PUBLIC `fetchCraftRoomPage` (GET /craft-rooms/{screenName},
                  no auth), so it must NOT be wrapped in <AuthRoute> (that redirected non-authed
                  visitors to "/"). Owner-only actions live on their own gated sub-routes below.
                */}
                <CraftRoom />
              </Suspense>
            }
          />
          <Route
            path="craft-rooms/:screenName/materials/:materialId"
            element={
              <Suspense fallback={<PageLoader />}>
                {/*
                  Public view (matches the public craft-room route above): PersonalMaterialPage has an
                  anonymous branch (fetchMaterialPage — public API with stripped fields) for non-owners,
                  so viewing a material in someone's craft room must not be gated. A visitor clicking a
                  material in a public craft room lands here; <AuthRoute> would have bounced them to "/".
                  The owner-only EDIT route below stays gated.
                */}
                <PersonalMaterialDetailPage />
              </Suspense>
            }
          />
          <Route
            path="craft-rooms/:screenName/materials/:materialId/edit"
            element={
              <Suspense fallback={<PageLoader />}>
                <AuthRoute><EditPersonalMaterialPage /></AuthRoute>
              </Suspense>
            }
          />

          <Route
            path="materials/:materialId/:materialSlug?"
            element={
              <Suspense fallback={<PageLoader />}>
                <PublicMaterialDetailPage />
              </Suspense>
            }
          />
          <Route
            path="materials/create"
            element={
              <Suspense fallback={<PageLoader />}>
                <AuthRoute><MaterialCreationPage /></AuthRoute>
              </Suspense>
            }
          />
          <Route
            path="craft-room/materials/add"
            element={
              <Suspense fallback={<PageLoader />}>
                <AuthRoute><MaterialCreationPage /></AuthRoute>
              </Suspense>
            }
          />
          <Route
            path="materials/discover"
            element={
              <Suspense fallback={<PageLoader />}>
                <MaterialDiscoveryPage />
              </Suspense>
            }
          />
          <Route
            path="craft-projects/create"
            element={
              <Suspense fallback={<PageLoader />}>
                <AuthRoute><CreateCraftProjectPage /></AuthRoute>
              </Suspense>
            }
          />
          <Route
            path="craft-projects/:projectId"
            element={
              <Suspense fallback={<PageLoader />}>
                <CraftProjectDetailPage />
              </Suspense>
            }
          />
          <Route
            path="craft-projects/:projectId/edit"
            element={
              <Suspense fallback={<PageLoader />}>
                <AuthRoute><EditCraftProjectPage /></AuthRoute>
              </Suspense>
            }
          />

          <Route
            path="inventory"
            element={
              <Suspense fallback={<PageLoader />}>
                <AuthRoute><InventoryPage /></AuthRoute>
              </Suspense>
            }
          />
          <Route
            path="test/public-materials"
            element={
              <Suspense fallback={<PageLoader />}>
                <PublicMaterialsTestPage />
              </Suspense>
            }
          />
          <Route
            path="test/create-parent-material"
            element={
              <Suspense fallback={<PageLoader />}>
                <CreateParentMaterialPage />
              </Suspense>
            }
          />
          <Route
            path="craft-rooms/:username/materials/:materialId/link"
            element={
              <Suspense fallback={<PageLoader />}>
                <AuthRoute><LinkMaterialPage /></AuthRoute>
              </Suspense>
            }
          />
          <Route
            path="pending-link-confirmations"
            element={
              <Suspense fallback={<PageLoader />}>
                <AuthRoute><PendingLinkConfirmationsPage /></AuthRoute>
              </Suspense>
            }
          />
          <Route
            path="my-collections"
            element={
              <Suspense fallback={<PageLoader />}>
                <AuthRoute><MyCollectionsPage /></AuthRoute>
              </Suspense>
            }
          />
          <Route
            path="my-collections/:collectionId"
            element={
              <Suspense fallback={<PageLoader />}>
                <AuthRoute><CollectionViewPage /></AuthRoute>
              </Suspense>
            }
          />
          <Route
            path="collections/search"
            element={
              <Suspense fallback={<PageLoader />}>
                <CollectionSearchPage />
              </Suspense>
            }
          />
          <Route
            path="collections/:collectionId"
            element={
              <Suspense fallback={<PageLoader />}>
                <CollectionViewPage />
              </Suspense>
            }
          />
          <Route
            path="brand/:brandName"
            element={
              <Suspense fallback={<PageLoader />}>
                <BrandPage />
              </Suspense>
            }
          />
          <Route
            path="supporting-artists"
            element={
              <Suspense fallback={<PageLoader />}>
                <SupportingArtistsPage />
              </Suspense>
            }
          />
          <Route
            path="my-field-suggestions/:suggestionId"
            element={
              <Suspense fallback={<PageLoader />}>
                <AuthRoute><MyFieldSuggestionsPage /></AuthRoute>
              </Suspense>
            }
          />
        </Route>
      </Routes>
    </>
  )

  return (
    <>
      {appContent}
    </>
  )
}
export default App
